import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Bot, User, Sparkles, Maximize2, Minimize2, Volume2, VolumeX, Mic, MicOff } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API_BASE_URL = window.location.hostname === 'localhost'
    ? 'http://localhost:5000/api'
    : '/api';

const STARTER_PROMPTS = [
    "What is a DEX and how does it work?",
    "Explain smart contract security risks",
    "What are the best L2 solutions right now?",
    "How does cross-chain bridging work?",
    "What is DeFi liquidity provision?"
];

export default function ClaudeBot() {
    const [isOpen, setIsOpen] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [messages, setMessages] = useState([
        {
            id: 1,
            role: 'assistant',
            content: "Hey builder 👋 I'm your Web3 AI assistant. Ask me anything about DeFi, smart contracts, blockchain architecture, or Web3 tooling.",
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [lastProvider, setLastProvider] = useState(null);
    const [speakingMsgId, setSpeakingMsgId] = useState(null);
    const [isListening, setIsListening] = useState(false);
    const messagesEndRef = useRef(null);
    const audioRef = useRef(null);
    const recognitionRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping, isOpen, isFullScreen]);

    useEffect(() => {
        const handleOpenClaude = () => setIsOpen(true);
        window.addEventListener('open-claude', handleOpenClaude);
        return () => window.removeEventListener('open-claude', handleOpenClaude);
    }, []);

    // Cleanup audio on unmount
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            window.speechSynthesis?.cancel();
        };
    }, []);

    /* ── TTS: Speak a message via ElevenLabs ── */
    const speakMessage = async (msgId, text) => {
        // If already speaking this message, stop
        if (speakingMsgId === msgId) {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            window.speechSynthesis?.cancel();
            setSpeakingMsgId(null);
            return;
        }

        // Stop any currently playing audio
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        window.speechSynthesis?.cancel();

        setSpeakingMsgId(msgId);

        // Strip markdown for cleaner speech
        const cleanText = text
            .replace(/#{1,6}\s/g, '')
            .replace(/\*\*(.*?)\*\*/g, '$1')
            .replace(/\*(.*?)\*/g, '$1')
            .replace(/`{1,3}[^`]*`{1,3}/g, '')
            .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
            .replace(/[⚠️👋🔴🟡🟢✅]/g, '')
            .trim();

        if (!cleanText) {
            setSpeakingMsgId(null);
            return;
        }

        try {
            const res = await fetch(`${API_BASE_URL}/tts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: cleanText.substring(0, 2000) })
            });

            if (!res.ok) throw new Error('ElevenLabs TTS failed');

            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            audioRef.current = audio;
            audio.onended = () => {
                setSpeakingMsgId(null);
                audioRef.current = null;
                URL.revokeObjectURL(url);
            };
            audio.onerror = () => {
                setSpeakingMsgId(null);
                audioRef.current = null;
            };
            await audio.play();
        } catch (err) {
            console.warn('ElevenLabs fallback to browser synthesis:', err);
            const utterance = new SpeechSynthesisUtterance(cleanText.substring(0, 1000));
            utterance.rate = 0.95;
            utterance.pitch = 1;
            utterance.onend = () => setSpeakingMsgId(null);
            utterance.onerror = () => setSpeakingMsgId(null);
            window.speechSynthesis.speak(utterance);
        }
    };

    /* ── Speech-to-Text: Voice input ── */
    const toggleListening = () => {
        if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
            alert('Speech recognition is not supported in your browser.');
            return;
        }

        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
            return;
        }

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-US';

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            setInputValue(prev => prev + transcript);
            setIsListening(false);
        };

        recognition.onerror = () => setIsListening(false);
        recognition.onend = () => setIsListening(false);

        recognitionRef.current = recognition;
        recognition.start();
        setIsListening(true);
    };

    const sendMessage = async (text) => {
        if (!text?.trim() || isTyping) return;

        const newUserMessage = {
            id: Date.now(),
            role: 'user',
            content: text.trim()
        };

        setMessages(prev => [...prev, newUserMessage]);
        setInputValue('');
        setIsTyping(true);

        try {
            const conversationHistory = [...messages.filter(m => m.id !== 1), newUserMessage]
                .map(m => ({ role: m.role, content: m.content }));

            const response = await fetch(`${API_BASE_URL}/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ messages: conversationHistory })
            });

            const data = await response.json();

            if (data.success) {
                setLastProvider(data.provider || 'openai');
                setMessages(prev => [...prev, {
                    id: Date.now(),
                    role: 'assistant',
                    content: data.reply,
                    provider: data.provider
                }]);
            } else {
                const backendMessage = data?.error?.message || data?.message || 'Something went wrong. Please try again.';
                const requestRef = data?.requestId ? `\n(ref: ${data.requestId})` : '';
                setMessages(prev => [...prev, {
                    id: Date.now(),
                    role: 'assistant',
                    content: `⚠️ ${backendMessage}${requestRef}`
                }]);
            }
        } catch (err) {
            setMessages(prev => [...prev, {
                id: Date.now(),
                role: 'assistant',
                content: '⚠️ Unable to reach the AI service. Please check your connection and try again.'
            }]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleSendMessage = (e) => {
        e?.preventDefault();
        sendMessage(inputValue);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Chat window variants
    const windowVariants = {
        hidden: { opacity: 0, y: 50, scale: 0.9, originX: 1, originY: 1 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { type: "spring", stiffness: 300, damping: 25 }
        },
        exit: {
            opacity: 0,
            y: 20,
            scale: 0.9,
            transition: { duration: 0.2 }
        }
    };

    const renderMarkdownMessage = (content) => (
        <ReactMarkdown
            components={{
                h1: ({ children }) => <h1 className="text-base font-extrabold text-slate-900 mt-2 mb-2">{children}</h1>,
                h2: ({ children }) => <h2 className="text-[15px] font-bold text-slate-900 mt-2 mb-2">{children}</h2>,
                h3: ({ children }) => <h3 className="text-sm font-bold text-slate-900 mt-2 mb-1.5">{children}</h3>,
                p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                ul: ({ children }) => <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>,
                li: ({ children }) => <li>{children}</li>,
                blockquote: ({ children }) => (
                    <blockquote className="border-l-2 border-purple-300 pl-3 italic text-gray-700 my-2">{children}</blockquote>
                ),
                strong: ({ children }) => <strong className="font-bold text-slate-900">{children}</strong>,
                em: ({ children }) => <em className="italic">{children}</em>,
                code: ({ inline, className, children, ...props }) => {
                    const match = /language-(\w+)/.exec(className || '');
                    if (inline) {
                        return (
                            <code className="bg-slate-200 text-purple-700 px-1 py-0.5 rounded text-xs font-mono" {...props}>
                                {children}
                            </code>
                        );
                    }
                    return (
                        <pre className="bg-slate-900 text-green-400 rounded-lg p-3 my-2 overflow-x-auto text-xs font-mono">
                            {match?.[1] && <div className="text-slate-500 text-[10px] mb-1 uppercase">{match[1]}</div>}
                            <code {...props}>{children}</code>
                        </pre>
                    );
                },
            }}
        >
            {content || ''}
        </ReactMarkdown>
    );

    return (
        <>
            {/* Chat Window */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        variants={windowVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className={`fixed z-50 bg-white shadow-2xl flex flex-col border border-gray-200
                            ${isFullScreen
                                ? 'inset-0 rounded-none'
                                : 'bottom-6 right-6 w-[420px] h-[600px] rounded-2xl'
                            }`}
                    >
                        {/* Header */}
                        <div className={`flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-slate-900 to-purple-900 text-white ${isFullScreen ? '' : 'rounded-t-2xl'}`}>
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                                    <Sparkles size={18} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-sm">Web3Central AI</h3>
                                    <p className="text-[11px] text-purple-300">
                                        {speakingMsgId ? '🔊 Speaking...' : 'AI assistant · Voice enabled'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setIsFullScreen(!isFullScreen)}
                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                >
                                    {isFullScreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                                </button>
                                <button
                                    onClick={() => { setIsOpen(false); setIsFullScreen(false); }}
                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
                            {messages.map((msg) => (
                                <motion.div
                                    key={msg.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                                >
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm ${msg.role === 'user'
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-gradient-to-br from-slate-800 to-indigo-900 text-white'
                                        }`}>
                                        {msg.role === 'user' ? <User size={14} /> : <Bot size={14} />}
                                    </div>
                                    <div className={`max-w-[80%] px-3.5 py-2.5 rounded-xl text-sm leading-relaxed shadow-sm ${msg.role === 'user'
                                        ? 'bg-purple-600 text-white rounded-br-none'
                                        : 'bg-white text-gray-800 border border-gray-100 rounded-bl-none'
                                        }`}>
                                        <div className="break-words">
                                            {msg.role === 'assistant'
                                                ? renderMarkdownMessage(msg.content)
                                                : <span className="whitespace-pre-wrap">{msg.content}</span>}
                                        </div>
                                        
                                        {/* Footer: provider + speak button */}
                                        {msg.role === 'assistant' && (
                                            <div className="mt-2 pt-2 border-t border-gray-50 flex items-center justify-between">
                                                {msg.provider && (
                                                    <span className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">
                                                        via {msg.provider === 'openai' ? 'OpenAI' : msg.provider === 'grok' ? 'Grok' : 'Fallback'}
                                                    </span>
                                                )}
                                                <button
                                                    onClick={() => speakMessage(msg.id, msg.content)}
                                                    className={`ml-auto p-1.5 rounded-lg transition-all ${
                                                        speakingMsgId === msg.id
                                                            ? 'bg-purple-100 text-purple-600 shadow-sm'
                                                            : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                                                    }`}
                                                    title={speakingMsgId === msg.id ? 'Stop speaking' : 'Listen to this message'}
                                                >
                                                    {speakingMsgId === msg.id ? (
                                                        <VolumeX size={14} className="animate-pulse" />
                                                    ) : (
                                                        <Volume2 size={14} />
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            ))}

                            {/* Starter prompt chips — shown only on fresh conversation */}
                            {messages.length === 1 && !isTyping && (
                                <div className="flex flex-wrap gap-2 mt-2 pl-9">
                                    {STARTER_PROMPTS.map(prompt => (
                                        <button
                                            key={prompt}
                                            onClick={() => sendMessage(prompt)}
                                            className="text-[11px] font-medium px-3 py-1.5 bg-white border border-purple-100 text-purple-700 rounded-full hover:bg-purple-50 hover:border-purple-300 transition-all shadow-sm"
                                        >
                                            {prompt}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {isTyping && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="flex gap-2.5"
                                >
                                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-slate-800 to-indigo-900 text-white flex items-center justify-center shadow-sm">
                                        <Bot size={14} />
                                    </div>
                                    <div className="bg-white border border-gray-100 px-4 py-3 rounded-xl rounded-bl-none shadow-sm">
                                        <div className="flex gap-1.5">
                                            <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                            <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                            <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input */}
                        <form
                            onSubmit={handleSendMessage}
                            className={`p-3 border-t border-gray-100 bg-white ${isFullScreen ? '' : 'rounded-b-2xl'}`}
                        >
                            <div className="flex items-end gap-2">
                                {/* Mic button */}
                                <button
                                    type="button"
                                    onClick={toggleListening}
                                    className={`p-2.5 rounded-xl transition-all shrink-0 ${
                                        isListening
                                            ? 'bg-red-500 text-white shadow-md shadow-red-200 animate-pulse'
                                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                                    }`}
                                    title={isListening ? 'Stop listening' : 'Voice input'}
                                >
                                    {isListening ? <MicOff size={16} /> : <Mic size={16} />}
                                </button>
                                <textarea
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={isListening ? "Listening..." : "Ask about DeFi, smart contracts..."}
                                    rows={1}
                                    className="flex-1 resize-none border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gray-50 placeholder-gray-400"
                                    style={{ maxHeight: '100px' }}
                                    disabled={isTyping}
                                />
                                <button
                                    type="submit"
                                    disabled={isTyping || !inputValue.trim()}
                                    className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                                >
                                    <Send size={16} />
                                </button>
                            </div>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Floating Button — only visible when chat is closed */}
            {!isOpen && (
                <motion.button
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setIsOpen(true)}
                    className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-700 text-white rounded-2xl flex items-center justify-center shadow-xl hover:shadow-2xl transition-shadow"
                >
                    <MessageSquare size={22} />
                </motion.button>
            )}
        </>
    );
}
